import { lazy, Suspense } from 'react'
import { createBrowserRouter } from 'react-router-dom'
import { App } from '@/App'
import { SubdomainStorefront } from '@/components/storefront/SubdomainStorefront'
import { ProtectedRoute, AdminRoute } from './ProtectedRoute'
import { LoadingSpinner } from '@/components/common/LoadingSpinner'

// Critical pages loaded eagerly (initial render path)
import { HomePage } from '@/pages/Home/HomePage'
import { BrowsePage } from '@/pages/Browse/BrowsePage'
import { ListingPage } from '@/pages/Listing/ListingPage'
import { NotFoundPage } from '@/pages/NotFound/NotFoundPage'

/**
 * Wrapper around lazy() that auto-reloads on chunk load failure.
 * After a deploy, cached HTML may reference old chunk filenames.
 * This catches the error and does a single hard reload to fetch fresh HTML.
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
function lazyWithRetry(importFn: () => Promise<any>) {
  return lazy(() =>
    importFn().catch(() => {
      const hasReloaded = sessionStorage.getItem('chunk-reload');
      if (!hasReloaded) {
        sessionStorage.setItem('chunk-reload', '1');
        window.location.reload();
        // Return a never-resolving promise while the page reloads
        return new Promise(() => {});
      }
      sessionStorage.removeItem('chunk-reload');
      return importFn();
    })
  );
}

// Lazy-loaded pages (code-split for smaller initial bundle)
const CategoriesPage = lazyWithRetry(() => import('@/pages/Categories/CategoriesPage').then(m => ({ default: m.CategoriesPage })))
const CreateListingPage = lazyWithRetry(() => import('@/pages/Create/CreateListingPage').then(m => ({ default: m.CreateListingPage })))
const ProfilePage = lazyWithRetry(() => import('@/pages/Profile/ProfilePage').then(m => ({ default: m.ProfilePage })))
const EditProfilePage = lazyWithRetry(() => import('@/pages/Profile/EditProfilePage').then(m => ({ default: m.EditProfilePage })))
const AccountSettingsPage = lazyWithRetry(() => import('@/pages/Profile/AccountSettingsPage').then(m => ({ default: m.AccountSettingsPage })))
const BugReportPage = lazyWithRetry(() => import('@/pages/BugReport/BugReportPage').then(m => ({ default: m.BugReportPage })))
const MyBugReportsPage = lazyWithRetry(() => import('@/pages/BugReport/MyBugReportsPage').then(m => ({ default: m.MyBugReportsPage })))
const StorefrontPage = lazyWithRetry(() => import('@/pages/Storefront/StorefrontPage').then(m => ({ default: m.StorefrontPage })))
const MyListingsPage = lazyWithRetry(() => import('@/pages/Profile/MyListingsPage').then(m => ({ default: m.MyListingsPage })))
const SavedItemsPage = lazyWithRetry(() => import('@/pages/Profile/SavedItemsPage').then(m => ({ default: m.SavedItemsPage })))
const WatchlistPage = lazyWithRetry(() => import('@/pages/Profile/WatchlistPage').then(m => ({ default: m.WatchlistPage })))
const ReviewsPage = lazyWithRetry(() => import('@/pages/Profile/ReviewsPage').then(m => ({ default: m.ReviewsPage })))
const MilestonesPage = lazyWithRetry(() => import('@/pages/Profile/MilestonesPage').then(m => ({ default: m.MilestonesPage })))
const OffersPage = lazyWithRetry(() => import('@/pages/Profile/OffersPage').then(m => ({ default: m.OffersPage })))
const MessagesLayout = lazyWithRetry(() => import('@/pages/Messages/MessagesLayout').then(m => ({ default: m.MessagesLayout })))
const CheckoutPage = lazyWithRetry(() => import('@/pages/Checkout/CheckoutPage').then(m => ({ default: m.CheckoutPage })))
const OrdersPage = lazyWithRetry(() => import('@/pages/Orders/OrdersPage').then(m => ({ default: m.OrdersPage })))
const SellerOrdersPage = lazyWithRetry(() => import('@/pages/Orders/SellerOrdersPage').then(m => ({ default: m.SellerOrdersPage })))
const OrderDetailPage = lazyWithRetry(() => import('@/pages/Orders/OrderDetailPage').then(m => ({ default: m.OrderDetailPage })))
const NotificationsPage = lazyWithRetry(() => import('@/pages/Notifications/NotificationsPage').then(m => ({ default: m.NotificationsPage })))
const AboutPage = lazyWithRetry(() => import('@/pages/Static/AboutPage').then(m => ({ default: m.AboutPage })))
const HowItWorksPage = lazyWithRetry(() => import('@/pages/Static/HowItWorksPage').then(m => ({ default: m.HowItWorksPage })))
const FAQPage = lazyWithRetry(() => import('@/pages/Static/FAQPage').then(m => ({ default: m.FAQPage })))
const ContactPage = lazyWithRetry(() => import('@/pages/Static/ContactPage').then(m => ({ default: m.ContactPage })))
const PricingPage = lazyWithRetry(() => import('@/pages/Static/PricingPage').then(m => ({ default: m.PricingPage })))
const PrivacyPage = lazyWithRetry(() => import('@/pages/Static/PrivacyPage').then(m => ({ default: m.PrivacyPage })))
const TermsPage = lazyWithRetry(() => import('@/pages/Static/TermsPage').then(m => ({ default: m.TermsPage })))
const StyleGuidePage = lazyWithRetry(() => import('@/pages/Static/StyleGuidePage').then(m => ({ default: m.StyleGuidePage })))
const ClientBugsPage = lazyWithRetry(() => import('@/pages/Static/ClientBugsPage').then(m => ({ default: m.ClientBugsPage })))
const QAChecklistPage = lazyWithRetry(() => import('@/pages/Static/QAChecklistPage').then(m => ({ default: m.QAChecklistPage })))
const SitesProposalPage = lazyWithRetry(() => import('@/pages/Static/SitesProposalPage').then(m => ({ default: m.SitesProposalPage })))
const TrackerPage = lazyWithRetry(() => import('@/pages/Static/tracker/TrackerPage').then(m => ({ default: m.TrackerPage })))
const AdminPage = lazyWithRetry(() => import('@/pages/Admin/AdminPage').then(m => ({ default: m.AdminPage })))
const SignupPage = lazyWithRetry(() => import('@/pages/Auth/SignupPage').then(m => ({ default: m.SignupPage })))
const AuthActionPage = lazyWithRetry(() => import('@/pages/Auth/AuthActionPage').then(m => ({ default: m.AuthActionPage })))

function PageLoader() {
  return (
    <div className="flex items-center justify-center min-h-[60vh]">
      <LoadingSpinner size="lg" />
    </div>
  )
}

function SuspenseWrap({ children }: { children: React.ReactNode }) {
  return <Suspense fallback={<PageLoader />}>{children}</Suspense>
}

/**
 * Create a router for subdomain access (e.g., storename.isqroll.co.nz).
 * The index route renders the StorefrontPage for the detected slug.
 * All other routes (listing pages, auth, etc.) still work normally.
 */
export function createSubdomainRouter(slug: string) {
  return createBrowserRouter([
    {
      path: '/',
      element: <App />,
      children: [
        // Subdomain index → storefront for this slug
        { index: true, element: <SuspenseWrap><SubdomainStorefront slug={slug} /></SuspenseWrap> },
        // All other routes remain functional on subdomains
        { path: 'browse', element: <BrowsePage /> },
        { path: 'categories', element: <SuspenseWrap><CategoriesPage /></SuspenseWrap> },
        { path: 'listing/:id', element: <ListingPage /> },
        { path: 'seller/:sellerId', element: <SuspenseWrap><StorefrontPage /></SuspenseWrap> },
        { path: 'store/:slug', element: <SuspenseWrap><StorefrontPage /></SuspenseWrap> },
        { path: 'about', element: <SuspenseWrap><AboutPage /></SuspenseWrap> },
        { path: 'how-it-works', element: <SuspenseWrap><HowItWorksPage /></SuspenseWrap> },
        { path: 'faq', element: <SuspenseWrap><FAQPage /></SuspenseWrap> },
        { path: 'contact', element: <SuspenseWrap><ContactPage /></SuspenseWrap> },
        { path: 'pricing', element: <SuspenseWrap><PricingPage /></SuspenseWrap> },
        { path: 'privacy', element: <SuspenseWrap><PrivacyPage /></SuspenseWrap> },
        { path: 'terms', element: <SuspenseWrap><TermsPage /></SuspenseWrap> },
        { path: 'signup', element: <SuspenseWrap><SignupPage /></SuspenseWrap> },
        {
          element: <ProtectedRoute />,
          children: [
            { path: 'create', element: <SuspenseWrap><CreateListingPage /></SuspenseWrap> },
            { path: 'listing/:id/edit', element: <SuspenseWrap><CreateListingPage /></SuspenseWrap> },
            { path: 'profile', element: <SuspenseWrap><ProfilePage /></SuspenseWrap> },
            { path: 'profile/edit', element: <SuspenseWrap><EditProfilePage /></SuspenseWrap> },
            { path: 'profile/settings', element: <SuspenseWrap><AccountSettingsPage /></SuspenseWrap> },
            { path: 'messages', element: <SuspenseWrap><MessagesLayout /></SuspenseWrap> },
            { path: 'messages/:conversationId', element: <SuspenseWrap><MessagesLayout /></SuspenseWrap> },
            { path: 'checkout', element: <SuspenseWrap><CheckoutPage /></SuspenseWrap> },
            { path: 'orders', element: <SuspenseWrap><OrdersPage /></SuspenseWrap> },
            { path: 'orders/:id', element: <SuspenseWrap><OrderDetailPage /></SuspenseWrap> },
            { path: 'cart', element: <SuspenseWrap><CheckoutPage /></SuspenseWrap> },
          ],
        },
        { path: '*', element: <NotFoundPage /> },
      ],
    },
  ])
}

export const router = createBrowserRouter([
  {
    path: '/',
    element: <App />,
    children: [
      // Public routes (eagerly loaded — critical path)
      { index: true, element: <HomePage /> },
      { path: 'browse', element: <BrowsePage /> },
      { path: 'categories', element: <SuspenseWrap><CategoriesPage /></SuspenseWrap> },
      { path: 'listing/:id', element: <ListingPage /> },
      { path: 'seller/:sellerId', element: <SuspenseWrap><StorefrontPage /></SuspenseWrap> },
      { path: 'store/:slug', element: <SuspenseWrap><StorefrontPage /></SuspenseWrap> },

      // Static pages (lazy-loaded)
      { path: 'about', element: <SuspenseWrap><AboutPage /></SuspenseWrap> },
      { path: 'how-it-works', element: <SuspenseWrap><HowItWorksPage /></SuspenseWrap> },
      { path: 'faq', element: <SuspenseWrap><FAQPage /></SuspenseWrap> },
      { path: 'contact', element: <SuspenseWrap><ContactPage /></SuspenseWrap> },
      { path: 'pricing', element: <SuspenseWrap><PricingPage /></SuspenseWrap> },
      { path: 'privacy', element: <SuspenseWrap><PrivacyPage /></SuspenseWrap> },
      { path: 'terms', element: <SuspenseWrap><TermsPage /></SuspenseWrap> },
      { path: 'styleguide', element: <SuspenseWrap><StyleGuidePage /></SuspenseWrap> },
      { path: 'tracker', element: <SuspenseWrap><TrackerPage /></SuspenseWrap> },
      { path: 'clientbugs', element: <SuspenseWrap><ClientBugsPage /></SuspenseWrap> },
      { path: 'qa-checklist', element: <SuspenseWrap><QAChecklistPage /></SuspenseWrap> },
      { path: 'bug-report', element: <SuspenseWrap><BugReportPage /></SuspenseWrap> },
      { path: 'sites', element: <SuspenseWrap><SitesProposalPage /></SuspenseWrap> },
      { path: 'signup', element: <SuspenseWrap><SignupPage /></SuspenseWrap> },
      { path: 'auth/action', element: <SuspenseWrap><AuthActionPage /></SuspenseWrap> },

      // Protected routes (lazy-loaded, require authentication)
      {
        element: <ProtectedRoute />,
        children: [
          { path: 'create', element: <SuspenseWrap><CreateListingPage /></SuspenseWrap> },
          { path: 'listing/:id/edit', element: <SuspenseWrap><CreateListingPage /></SuspenseWrap> },
          { path: 'profile', element: <SuspenseWrap><ProfilePage /></SuspenseWrap> },
          { path: 'profile/edit', element: <SuspenseWrap><EditProfilePage /></SuspenseWrap> },
          { path: 'profile/settings', element: <SuspenseWrap><AccountSettingsPage /></SuspenseWrap> },
          { path: 'my-listings', element: <SuspenseWrap><MyListingsPage /></SuspenseWrap> },
          { path: 'saved', element: <SuspenseWrap><SavedItemsPage /></SuspenseWrap> },
          { path: 'watchlist', element: <SuspenseWrap><WatchlistPage /></SuspenseWrap> },
          { path: 'reviews', element: <SuspenseWrap><ReviewsPage /></SuspenseWrap> },
          { path: 'milestones', element: <SuspenseWrap><MilestonesPage /></SuspenseWrap> },
          { path: 'offers', element: <SuspenseWrap><OffersPage /></SuspenseWrap> },
          { path: 'messages', element: <SuspenseWrap><MessagesLayout /></SuspenseWrap> },
          { path: 'messages/:conversationId', element: <SuspenseWrap><MessagesLayout /></SuspenseWrap> },
          { path: 'checkout', element: <SuspenseWrap><CheckoutPage /></SuspenseWrap> },
          { path: 'orders', element: <SuspenseWrap><OrdersPage /></SuspenseWrap> },
          { path: 'orders/:id', element: <SuspenseWrap><OrderDetailPage /></SuspenseWrap> },
          { path: 'seller-orders', element: <SuspenseWrap><SellerOrdersPage /></SuspenseWrap> },
          { path: 'notifications', element: <SuspenseWrap><NotificationsPage /></SuspenseWrap> },
          { path: 'bug-report/status', element: <SuspenseWrap><MyBugReportsPage /></SuspenseWrap> },
        ],
      },

      // Admin-only routes (lazy-loaded, require admin access)
      {
        element: <AdminRoute />,
        children: [
          { path: 'admin', element: <SuspenseWrap><AdminPage /></SuspenseWrap> },
        ],
      },

      // Catch-all
      { path: '*', element: <NotFoundPage /> },
    ],
  },
])
